import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { resolveCanonicalAdminUrl } from '@kawai/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AuthProvider } from './lib/auth.js';
import { auth } from './lib/firebase.js';
import { consumeSsoTokenFromHash } from '@kawai/auth-bootstrap';
// S-136: v2 の書体（Noto Sans JP）を同梱する。CSS の @import だと Tailwind v4 がインライン化して
// woff2 の URL が解決されないため main.tsx で import する（packages/ui/README.md）。
import '@fontsource-variable/noto-sans-jp';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// T-08: tenant-portal からの遷移時に URL fragment 経由で渡された sso_token を消費し、
// AuthProvider が onAuthStateChanged を最初に評価する前に sign-in を完了させる。
// fragment が無い / sign-in 失敗時は通常の起動フローに fall-through する。
async function bootstrap(): Promise<void> {
  await consumeSsoTokenFromHash(auth, window.location.hash);
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// TP-33: 旧ドメイン（apollostation サブドメイン / *.web.app）で開かれた場合は
// 正規ドメイン（tracepit.jp）へ引っ越す（path / query / hash 維持）。旧ブックマークにも
// same-site 化の恩恵を届け、旧オリジンへのセッション・記録の蓄積を止める。
const canonicalUrl = resolveCanonicalAdminUrl(
  'fuel',
  window.location.host,
  import.meta.env.MODE,
);
if (canonicalUrl) {
  window.location.replace(
    canonicalUrl.replace(/\/$/, '') +
      window.location.pathname +
      window.location.search +
      window.location.hash,
  );
} else {
  void bootstrap();
}
